<template>
  <!--
    Inputs for setting custom field values while creating a task or conversation. Nothing is saved here:
    the parent form sends the emitted values along with the create request.
    Renders as its own boxed section below the standard fields.
  -->
  <section v-if="editableFields.length > 0" class="custom-field-inputs">
    <div
      v-for="field in editableFields"
      :key="field.id"
      :class="{
        'custom-field-inputs__row--multiline': isMultilineText(field),
        'custom-field-inputs__row--editing': isEditing(field),
      }"
      class="custom-field-inputs__row"
    >
      <div :title="field.name" class="custom-field-inputs__label">
        <i :class="`ddi ddi-${iconFor(field)}`" />{{ field.name }}
      </div>

      <div class="custom-field-inputs__control">
        <!-- CHECKBOX -->
        <dd-checkbox
          v-if="field.customFieldType === ECustomFieldTypes.Checkbox"
          :model-value="!!drafts[field.id]"
          dd-size="medium"
          class="custom-field-inputs__checkbox"
          @change="(checked) => setDraft(field, checked)"
        />

        <!-- DROPDOWN: same colored select button as the detail page -->
        <select-button
          v-else-if="field.customFieldType === ECustomFieldTypes.Dropdown"
          :model-value="drafts[field.id] || NO_OPTION_ID"
          :options="selectOptionsFor(field)"
          :title="field.name"
          :size="size"
          class="custom-field-inputs__select"
          @update:model-value="(optionID) => setDraft(field, optionID)"
        />

        <!-- DATE -->
        <due-date-picker
          v-else-if="field.customFieldType === ECustomFieldTypes.Date"
          :model-value="drafts[field.id] || null"
          :size="size"
          :sheet-title="field.name"
          :button-title-prefix="field.name"
          clear-button-text="Clear date"
          class="custom-field-inputs__date"
          @update:model-value="(date) => setDraft(field, date)"
        />

        <!-- TEXT / NUMBER, EDIT MODE: mirrors the detail page editor -->
        <template v-else-if="isEditing(field)">
          <div
            :class="{ 'custom-field-inputs__editor--multiline': isMultilineText(field) }"
            class="custom-field-inputs__editor"
          >
            <!-- Free-form text grows with its content -->
            <el-input
              v-if="isMultilineText(field)"
              ref="editor"
              v-model="editDraft"
              :placeholder="placeholderFor(field)"
              :autosize="{ minRows: 1, maxRows: 8 }"
              :size="elementSize"
              type="textarea"
              maxlength="500"
              resize="none"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <el-input
              v-else-if="field.customFieldType === ECustomFieldTypes.Text"
              ref="editor"
              v-model="editDraft"
              :placeholder="placeholderFor(field)"
              :size="elementSize"
              maxlength="500"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <el-input
              v-else
              ref="editor"
              v-model="editDraft"
              :placeholder="placeholderFor(field)"
              :step="numberStepFor(field)"
              :size="elementSize"
              type="number"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <div class="custom-field-inputs__editor-actions">
              <dd-icon-button
                icon="check"
                type="rounded"
                size="small"
                color="green"
                title="Save"
                @click="commitEdit(field)"
              />
              <dd-icon-button
                icon="close"
                type="rounded"
                size="small"
                color="grey"
                title="Cancel"
                @click="cancelEdit()"
              />
              <dd-icon-button
                v-if="hasDraftValue(field)"
                danger
                icon="trash"
                type="rounded"
                size="small"
                color="grey"
                title="Clear value"
                @click="clearValue(field)"
              />
            </div>
          </div>
          <p v-if="editError" class="custom-field-inputs__error">{{ editError }}</p>
        </template>

        <!-- TEXT / NUMBER, DISPLAY MODE: click to edit -->
        <template v-else>
          <span
            v-if="hasDraftValue(field)"
            :class="{ 'custom-field-inputs__display--multiline': isMultilineText(field) }"
            class="custom-field-inputs__display custom-field-inputs__display--editable"
            title="Click to edit"
            @click="beginEdit(field)"
          >{{ displayTextFor(field) }}<i class="ddi ddi-edit" /></span>
          <span
            v-else
            class="custom-field-inputs__display custom-field-inputs__display--editable custom-field-inputs__display--empty"
            @click="beginEdit(field)"
          >Add a value</span>
        </template>

        <p v-if="errors[field.id] && !isEditing(field)" class="custom-field-inputs__error">{{ errors[field.id] }}</p>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Helpers */
import CustomFieldAssistant, {
  ECustomFieldPermissions,
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

/* Data types */
import { ICustomField } from '@/api/types/response.types'
import { ICustomFieldValueForCreate } from '@/api/types/request.types'
import {
  IIDNamePairWithColor,
  IValidationResult,
} from '@/types/data-objects/global'

/* Sentinel ID for the "None" entry in dropdown select buttons */
const NO_OPTION_ID = 0

export default defineComponent({
  name: 'CustomFieldInputs',

  props: {
    /* Every custom field the requester can see; only the ones they can edit are rendered */
    fields: {
      type: Array as () => ICustomField[],
      default: (): ICustomField[] => [],
    },
    /* Size passed through to the pickers and inputs ('small' or 'medium') */
    size: {
      type: String,
      default: 'small',
    },
  },

  emits: ['input'],

  data() {
    return {
      ECustomFieldTypes,
      NO_OPTION_ID,
      /* Committed value per field ID: string for text/number, option ID, YYYY-MM-DD, or boolean */
      drafts: {} as Record<number, any>,
      /* Errors found when the parent asks for a full validation on submit */
      errors: {} as Record<number, string>,
      /* Text/number fields edit one at a time, like the detail page */
      editingFieldID: null as number,
      editDraft: '' as any,
      editError: '',
    }
  },

  computed: {
    /* Element Plus has no medium size; its default size is the closest */
    elementSize(): 'default' | 'small' {
      return this.size === 'small' ? 'small' : 'default'
    },
    editableFields(): ICustomField[] {
      return this.fields.filter(f => this.canEdit(f) && f.showOnCreate)
    },
  },

  watch: {
    /* A different field set (e.g. the project changed) invalidates anything typed so far */
    fields: {
      deep: 1,
      handler() {
        this.reset()
      },
    },
  },

  methods: {
    canEdit(field: ICustomField): boolean {
      if (this.$permissions.isAdminOrOwner) {
        return true
      }
      const permission = this.$permissions.isGuest
        ? field.guestUserPermissions
        : field.normalUserPermissions
      return permission === ECustomFieldPermissions.ViewAndEdit
    },

    iconFor(field: ICustomField): string {
      return CustomFieldAssistant.getFieldTypeIcon(field.customFieldType)
    },

    isMultilineText(field: ICustomField): boolean {
      return CustomFieldAssistant.isMultilineText(field)
    },

    placeholderFor(field: ICustomField): string {
      return CustomFieldAssistant.placeholderFor(field)
    },

    numberStepFor(field: ICustomField): string {
      return CustomFieldAssistant.numberStepFor(field)
    },

    /* A grey "None" entry leads the list so the select button has something to show before a pick */
    selectOptionsFor(field: ICustomField): IIDNamePairWithColor[] {
      const placeholder: IIDNamePairWithColor = {
        id: NO_OPTION_ID,
        name: 'None',
        color: 'grey',
      }
      const options = (field.options || []).map(o => ({
        id: o.id,
        name: o.name,
        color: o.color,
      }))
      return [placeholder, ...options]
    },

    /* ---------- Display formatting ---------- */

    displayTextFor(field: ICustomField): string {
      const draft = this.drafts[field.id]
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          return String(draft)
        case ECustomFieldTypes.Number:
          return CustomFieldAssistant.formatNumber(
            field,
            Number(String(draft).trim()),
          )
        default:
          return ''
      }
    },

    /* ---------- Click-to-edit for text and numbers ---------- */

    isEditing(field: ICustomField): boolean {
      return this.editingFieldID === field.id
    },

    beginEdit(field: ICustomField) {
      const draft = this.drafts[field.id]
      this.editingFieldID = field.id
      this.editDraft =
        draft === null || draft === undefined ? '' : String(draft)
      this.editError = ''

      this.$nextTick(() => {
        const editor = this.$refs.editor as any
        const instance = Array.isArray(editor) ? editor[0] : editor
        if (instance && typeof instance.focus === 'function') {
          instance.focus()
        }
      })
    },

    cancelEdit() {
      this.editingFieldID = null
      this.editDraft = ''
      this.editError = ''
    },

    /* Validates the typed value against the field settings, then commits it. Blanking out clears the value. */
    commitEdit(field: ICustomField) {
      if (!this.isEditing(field)) {
        return
      }

      const raw = String(this.editDraft === null ? '' : this.editDraft)

      if (raw.trim().length === 0) {
        this.clearValue(field)
        return
      }

      const error =
        field.customFieldType === ECustomFieldTypes.Number
          ? CustomFieldAssistant.validateNumber(field, raw)
          : CustomFieldAssistant.validateText(field, raw)

      if (error) {
        this.editError = error
        return
      }

      this.setDraft(field, raw.trim())
      this.cancelEdit()
    },

    clearValue(field: ICustomField) {
      delete this.drafts[field.id]
      delete this.errors[field.id]
      this.emitValues()
      this.cancelEdit()
    },

    /* ---------- Value bookkeeping ---------- */

    setDraft(field: ICustomField, value: any) {
      this.drafts[field.id] = value
      delete this.errors[field.id]
      this.emitValues()
    },

    /* True when the draft holds something worth sending for this field */
    hasDraftValue(field: ICustomField): boolean {
      const draft = this.drafts[field.id]
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
        case ECustomFieldTypes.Number:
          return (
            String(draft === null || draft === undefined ? '' : draft).trim()
              .length > 0
          )
        case ECustomFieldTypes.Dropdown:
          return !!draft && draft !== NO_OPTION_ID
        case ECustomFieldTypes.Date:
          return !!draft
        case ECustomFieldTypes.Checkbox:
          // An unchecked box is the same as no value, so only checked boxes are sent
          return draft === true
        default:
          return false
      }
    },

    buildValue(field: ICustomField): ICustomFieldValueForCreate {
      const draft = this.drafts[field.id]
      const value = { customFieldID: field.id } as ICustomFieldValueForCreate
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          value.textData = String(draft).trim()
          break
        case ECustomFieldTypes.Number:
          value.numberData = Number(String(draft).trim())
          break
        case ECustomFieldTypes.Dropdown:
          value.optionID = draft
          break
        case ECustomFieldTypes.Date:
          value.dateData = draft
          break
        case ECustomFieldTypes.Checkbox:
          value.checkboxData = true
          break
      }
      return value
    },

    emitValues() {
      const values = this.editableFields
        .filter(f => this.hasDraftValue(f))
        .map(f => this.buildValue(f))
      this.$emit('input', values)
    },

    /*
     * Checks every filled-in field against its settings. Errors show inline under the offending
     * inputs and the first one is returned so the parent can surface it. Empty fields are fine:
     * every custom field is optional at creation.
     */
    validate(): IValidationResult {
      // Anything still sitting in an open editor has not been committed and is not sent
      this.cancelEdit()

      const errors = {} as Record<number, string>
      let firstError: string = null

      this.editableFields.forEach(field => {
        if (!this.hasDraftValue(field)) {
          return
        }
        const raw = String(this.drafts[field.id])
        let error: string = null
        if (field.customFieldType === ECustomFieldTypes.Text) {
          error = CustomFieldAssistant.validateText(field, raw)
        } else if (field.customFieldType === ECustomFieldTypes.Number) {
          error = CustomFieldAssistant.validateNumber(field, raw)
        }
        if (error) {
          errors[field.id] = error
          if (!firstError) {
            firstError = error
          }
        }
      })

      this.errors = errors

      if (firstError) {
        return { success: false, message: firstError }
      }
      return { success: true }
    },

    /* Clears everything typed so far. Used after a successful create and when the field set changes. */
    reset() {
      this.cancelEdit()
      this.drafts = {}
      this.errors = {}
      this.$emit('input', [])
    },
  },
})
</script>

<style lang="scss">
/* A boxed section that sits below the standard ".meta-section" on the create forms and echoes its look */
.custom-field-inputs {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding: 2px 14px;
  border-radius: 8px;
  border: $border--dotted-medium;

  @include media-breakpoint-down(sm) {
    padding: 2px 10px;
  }

  &__row {
    display: flex;
    align-items: center;
    padding: 12px 0;
    border-bottom: $border--dotted-light;
    @include flex-row-horizontal-spacing(8px);

    &:last-child {
      border-bottom: none;
    }

    /* Keep the label at the top as a textarea grows or an editor opens */
    &--multiline,
    &--editing {
      align-items: flex-start;

      .custom-field-inputs__label {
        padding-top: 6px;
      }
    }
  }

  &__label {
    @include lettering--p-sm;
    font-weight: $bold;
    min-width: 150px;
    max-width: 150px;
    @include single-line-ellipsis;

    > .ddi {
      color: var(--color-text-third);
      padding-right: 4px;
    }

    @include media-breakpoint-down(sm) {
      min-width: 100px;
      max-width: 100px;
    }
  }

  &__control {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__select {
    max-width: 100%;

    .select-button__button {
      max-width: 100%;
    }
  }

  /* Read-only text with a hover affordance, matching the detail page */
  &__display {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 6px;
    margin-left: -6px;
    border-radius: 4px;
    @include lettering--p-sm;
    color: var(--color-text-primary);
    word-break: break-word;

    .ddi-edit {
      margin-left: 8px;
      font-size: 12px;
      color: var(--color-text-third);
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
    }

    &--editable {
      cursor: pointer;

      &:hover {
        background-color: var(--color-background-secondary);

        .ddi-edit {
          opacity: 1;
        }
      }
    }

    &--empty {
      color: var(--color-text-third);
      font-style: italic;
    }

    /* Free-form text keeps the line breaks the user typed */
    &--multiline {
      white-space: pre-wrap;
      align-items: flex-start;

      .ddi-edit {
        margin-top: 3px;
      }
    }
  }

  &__editor {
    display: flex;
    align-items: center;
    width: 100%;
    @include flex-row-horizontal-spacing(8px);

    > .el-input,
    > .el-textarea {
      flex: 1;
      min-width: 0;
      max-width: 320px;
    }

    /* Keep the action buttons pinned to the top as the textarea grows */
    &--multiline {
      align-items: flex-start;

      > .el-textarea {
        max-width: 480px;
      }

      .custom-field-inputs__editor-actions {
        padding-top: 2px;
      }
    }
  }

  &__editor-actions {
    display: flex;
    flex-shrink: 0;
    @include flex-row-horizontal-spacing(4px);
  }

  &__error {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: $strawberry;
  }
}
</style>
